import {
    Component,
    ElementRef,
    EventEmitter,
    Injector,
    PLATFORM_ID,
    afterNextRender,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
    viewChildren,
    DestroyRef,
    Output
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { PDFDocument } from 'pdf-lib';

import { PdfFieldInfo, PdfPageInfo } from './models/pdf-field-info.model';
import { FedexPrefillData, ShippingChoice } from './models/fedex-shipping.model';
import { PdfFieldExtractorService } from './services/pdf-field-extractor.service';
import { PdfRendererService } from './services/pdf-renderer.service';
import { PdfFormFillService } from './services/pdf-form-fill.service';
import { FedexShippingModalComponent } from './components/fedex-shipping-modal/fedex-shipping-modal.component';

@Component({
    selector: 'app-pdf-form-filler',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FedexShippingModalComponent],
    templateUrl: './pdf-form-filler.component.html',
    styleUrl: './pdf-form-filler.component.scss',
})
export class PdfFormFillerComponent {
    pdfUrls = input<string[] | null>(null);
    fedexLoading = input<boolean>(false);
    fedexError = input<string | null>(null);
    submitPending = false;
    paymentCompleted = false;

    // Output event to bubble shipping choice to parent
    shippingChoiceEvent = output<ShippingChoice>();
    requestPayment = output<boolean>();
    pdfCompleteEvent = output<void>();
    @Output() pdfComplete = new EventEmitter<string>();

    selectedShippingChoice = signal<ShippingChoice | null>(null);

    private platformId = inject(PLATFORM_ID);
    private destroyRef = inject(DestroyRef);
    private router = inject(Router);
    private extractorService = inject(PdfFieldExtractorService);
    private rendererService = inject(PdfRendererService);
    private formFillService = inject(PdfFormFillService);
    private http = inject(HttpClient);

    fieldsInfo = signal<PdfFieldInfo[]>([]);
    pages = signal<PdfPageInfo[]>([]);
    loading = signal<boolean>(false);
    error = signal<string | null>(null);
    resultUrl = signal<string | null>(null);
    showResult = signal<boolean>(false);
    showFedexModal = signal<boolean>(false);
    shippingChoice = signal<ShippingChoice | null>(null);

    form = new FormGroup({});
    formValue = toSignal(this.form.valueChanges, { initialValue: {} });

    canvases = viewChildren<ElementRef<HTMLCanvasElement>>('pdfCanvas');

    private pdfjsDocs: any[] = [];
    private pdfBytesArray: ArrayBuffer[] = [];
    private loadedPdfUrls = signal<string[]>([]);
    private fedexTriggered = signal<boolean>(false);

    // Computed signal to filter fields per page in the template
    fieldsForPage = computed(() => {
        const fields = this.fieldsInfo();
        return (pageIndex: number) => fields.filter((f) => f.pageIndex === pageIndex);
    });

    isFormCompleteForFedex = computed(() => {
        const vals = this.formValue() as Record<string, any>;
        if (!vals) return false;

        const hasAddress =
            !!vals['address'] &&
            !!vals['zipcode'] &&
            !!vals['city'] &&
            !!vals['state'] &&
            !!vals['phone'];
        const hasName =
            !!vals['fullName'] || (!!vals['firstName'] && !!vals['lastName']);

        return hasAddress && hasName;
    });

    fedexPrefillData = computed<FedexPrefillData>(() => {
        const vals = this.formValue() as Record<string, any>;
        const findVal = (...keys: string[]) => {
            if (!vals) return '';
            for (const key of Object.keys(vals)) {
                const lower = key.toLowerCase();
                if (keys.some(k => lower.includes(k))) {
                    return vals[key] || '';
                }
            }
            return '';
        };
        return {
            fullName: findVal('fullname', 'full_name'),
            firstName: findVal('firstname', 'first_name', 'first'),
            lastName: findVal('lastname', 'last_name', 'last'),
            phone: findVal('phone', 'tel'),
            address: findVal('address', 'street'),
            city: findVal('city'),
            state: findVal('state'),
            zipcode: findVal('zipcode', 'zip_code', 'zip'),
            email: findVal('email', 'e-mail', 'mail'),
        };
    });

    constructor() {
        const injector = inject(Injector);

        // SSR guard
        afterNextRender(() => {
            // 1. Reactive PDF loading
            effect(
                () => {
                    const urls = this.pdfUrls();
                    const currentUrlsStr = JSON.stringify(this.loadedPdfUrls());
                    const newUrlsStr = JSON.stringify(urls || []);
                    if (urls && urls.length > 0 && newUrlsStr !== currentUrlsStr) {
                        untracked(() => {
                            this.loadedPdfUrls.set(urls);
                            this.loadPdfs(urls);
                        });
                    }
                },
                { injector }
            );

            // 2. Reactive Canvas Rendering
            effect(
                () => {
                    const canvasElements = this.canvases();
                    const pages = this.pages();
                    const docs = this.pdfjsDocs;

                    if (
                        docs.length > 0 &&
                        canvasElements.length === pages.length &&
                        canvasElements.length > 0
                    ) {
                        untracked(() => {
                            this.renderAllPages(canvasElements);
                        });
                    }
                },
                { injector }
            );

            // 3. Reactive Fedex trigger
            effect(
                () => {
                    const isComplete = this.isFormCompleteForFedex();
                    if (isComplete && !this.fedexTriggered()) {
                        untracked(() => {
                            this.fedexTriggered.set(true);
                            this.showFedexModal.set(true);
                        });
                    }
                },
                { injector }
            );
        });
    }

    async loadPdfs(urls: string[]) {
        this.loading.set(true);
        this.error.set(null);
        try {
            this.pdfjsDocs = [];
            this.pdfBytesArray = [];

            const allFields: PdfFieldInfo[] = [];
            const allPages: PdfPageInfo[] = [];
            let globalPageIndex = 0;

            for (let docIdx = 0; docIdx < urls.length; docIdx++) {
                const url = urls[docIdx];
                const response = await fetch(url);
                if (!response.ok) {
                    console.warn(`Failed to fetch PDF at ${url}: ${response.statusText}`);
                    continue;
                }
                const bytes = await response.arrayBuffer();
                this.pdfBytesArray.push(bytes);

                // Extract fields
                const { fields } = await this.extractorService.extractFields(bytes);
                fields.forEach((f) => {
                    f.pageIndex += globalPageIndex;
                    allFields.push(f);
                });

                // Load with pdfjs-dist (clone the buffer so the worker doesn't detach the original)
                const pdfjsDoc = await this.rendererService.getPdfDocument(bytes.slice(0));
                this.pdfjsDocs.push(pdfjsDoc);

                for (let i = 1; i <= pdfjsDoc.numPages; i++) {
                    allPages.push({
                        index: globalPageIndex,
                        width: 0,
                        height: 0,
                        docIndex: docIdx,
                        docPageNumber: i
                    });
                    globalPageIndex++;
                }
            }

            this.fieldsInfo.set(allFields);
            this.pages.set(allPages);

            // Build Form
            const group: any = {};
            allFields.forEach((f) => {
                if (!group[f.name]) {
                    group[f.name] = new FormControl(f.type === 'checkbox' ? false : '');
                }
            });

            Object.keys(this.form.controls).forEach((k) => this.form.removeControl(k));
            Object.keys(group).forEach((k) => this.form.addControl(k, group[k]));

        } catch (err: any) {
            this.error.set(err.message || 'Error loading PDFs');
            console.error(err);
        } finally {
            this.loading.set(false);
        }
    }

    async renderAllPages(
        canvasElements: readonly ElementRef<HTMLCanvasElement>[]
    ) {
        const updatedPages = [...this.pages()];
        const currentFields = this.fieldsInfo();
        let fieldsUpdated = false;

        // Use a fixed width or compute from container
        const containerWidth =
            canvasElements[0].nativeElement.parentElement?.clientWidth || 800;

        for (let i = 0; i < canvasElements.length; i++) {
            const pageInfo = updatedPages[i];
            const canvas = canvasElements[i].nativeElement;
            const doc = this.pdfjsDocs[pageInfo.docIndex];

            const { viewport, pageSize } = await this.rendererService.renderPage(
                doc,
                pageInfo.docPageNumber,
                canvas,
                containerWidth
            );

            updatedPages[i].width = pageSize.width;
            updatedPages[i].height = pageSize.height;

            currentFields.forEach((field) => {
                if (field.pageIndex === i && field.rect) {
                    const rect = field.rect;
                    const pt1 = viewport.convertToViewportPoint(rect.x, rect.y);
                    const pt2 = viewport.convertToViewportPoint(rect.x + rect.width, rect.y + rect.height);
                    const viewportRect = [pt1[0], pt1[1], pt2[0], pt2[1]];

                    const left = Math.min(viewportRect[0], viewportRect[2]) + 12;
                    const top = Math.min(viewportRect[1], viewportRect[3]) + 9;
                    const width = Math.abs(viewportRect[2] - viewportRect[0]);
                    const height = Math.abs(viewportRect[3] - viewportRect[1]);

                    field.cssRect = { left, top, width, height };
                    fieldsUpdated = true;
                }
            });
        }

        if (fieldsUpdated) {
            this.fieldsInfo.set([...currentFields]);
        }
    }

    async submit() {
        if (!this.shippingChoice()) {
            this.submitPending = true;
            this.openFedexModal();
            return;
        }

        if (!this.paymentCompleted) {
            this.submitPending = true;
            const choice = this.shippingChoice();
            const isFedex = choice?.type === 'fedex';
            this.requestPayment.emit(isFedex);
            return;
        }

        if (this.pdfBytesArray.length === 0) return;

        this.loading.set(true);
        this.error.set(null);

        try {
            const values = this.form.value;
            const mergedPdf = await PDFDocument.create();

            for (const bytes of this.pdfBytesArray) {
                const pdfDoc = await PDFDocument.load(bytes);
                // Fill the fields using pdf-lib
                await this.formFillService.fillAndSave(pdfDoc, values);

                // Flatten to bake in the values before copying pages (so they survive the merge)
                const form = pdfDoc.getForm();
                form.flatten();

                // Copy all pages into the merged doc
                const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
                copiedPages.forEach((page) => mergedPdf.addPage(page));
            }

            const mergedBytes = await mergedPdf.save();
            const blob = new Blob([mergedBytes as any], { type: 'application/pdf' });

            const blobUrl = URL.createObjectURL(blob);

            // Auto-download the PDF
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = 'filled_form.pdf';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Print the PDF via a hidden iframe
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = blobUrl;
            document.body.appendChild(iframe);
            iframe.onload = () => {
                iframe.contentWindow?.print();

                const choice = this.selectedShippingChoice();
                if (choice && (choice.type === 'own' || choice.type === 'normal_mail')) {
                    setTimeout(() => {
                        this.router.navigate(['/thank-you']);
                    }, 1000);
                } else {
                    this.pdfCompleteEvent.emit();
                }
            };
        } catch (err: any) {
            this.error.set(err.message || 'Error processing PDF submission');
            this.loading.set(false);
        }
    }

    onShippingChoice(choice: ShippingChoice) {
        this.selectedShippingChoice.set(choice);
        this.shippingChoice.set(choice);
        this.shippingChoiceEvent.emit(choice);
    }

    openFedexModal() {
        this.showFedexModal.set(true);
    }

    onFedexClose(success: boolean = false) {
        this.showFedexModal.set(false);
        if (success && this.submitPending) {
            this.submit();
        } else if (!success) {
            this.submitPending = false;
        }
    }

    onPaymentClose(success: boolean = false) {
        if (success) {
            this.paymentCompleted = true;
            if (this.submitPending) {
                this.submit();
            }
        } else {
            this.submitPending = false;
        }
    }
}
